import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Toolbar,
    Typography,
    Paper,
    Checkbox,
    IconButton,
    Tooltip,
    FormControlLabel,
    Switch,
    Chip,
    Divider,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";

import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import { visuallyHidden } from '@mui/utils';
import { useState, useEffect, useMemo } from "react";
import PopoverComponent from '../Common/PopperComponent';
import ColorChips from '../Common/ColorChips';
import KillJobButton from './KillJobButton';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import JobActionsMenu from './JobActionsMenu';
import { useNavigate } from 'react-router-dom';
import { useToken } from '../items/UseToken';
import { JobsReadyState } from '../ReadyStates';

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// Cross-browser stable sort
function stableSort(array, comparator) {
    const stabilizedThis = (array || []).map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    { id: 'name', numeric: false, disablePadding: false, label: 'Robot Name' },
    { id: 'status', numeric: false, disablePadding: false, label: 'Status' },
    { id: 'guid', numeric: false, disablePadding: false, label: 'ID' },
    { id: 'machine', numeric: false, disablePadding: false, label: 'Machine' },
    { id: 'start_date', numeric: false, disablePadding: false, label: 'Start Date' },
    { id: 'end_date', numeric: false, disablePadding: false, label: 'End Date' },
    { id: 'duration', numeric: false, disablePadding: false, label: 'Duration' },
    { id: 'exception', numeric: false, disablePadding: false, label: 'Exception' },
];

function EnhancedTableHead(props) {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = (property) => (event) => onRequestSort(event, property);

    return (
        <TableRow>
            <TableCell padding="checkbox" sx={{ bgcolor: "background.paper" }}>
                <Checkbox
                    color="primary"
                    indeterminate={numSelected > 0 && numSelected < rowCount}
                    checked={rowCount > 0 && numSelected === rowCount}
                    onChange={onSelectAllClick}
                    inputProps={{ 'aria-label': 'select all jobs' }}
                />
            </TableCell>

            {headCells.map((headCell) => (
                <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    padding={headCell.disablePadding ? 'none' : 'normal'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                        bgcolor: "background.paper",
                        fontWeight: 900,
                        letterSpacing: 0.3,
                        whiteSpace: "nowrap",
                    }}
                >
                    <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={createSortHandler(headCell.id)}
                    >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                            <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                        ) : null}
                    </TableSortLabel>
                </TableCell>
            ))}

            {/* extra columns in body (Stop / Actions) */}
            <TableCell sx={{ bgcolor: "background.paper", fontWeight: 900, whiteSpace: "nowrap" }}>
                Controls
            </TableCell>
            <TableCell sx={{ bgcolor: "background.paper", fontWeight: 900, whiteSpace: "nowrap" }}>
                Actions
            </TableCell>
        </TableRow>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

export default function JobTable({ emptyState }) {
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('start_date');
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [dense, setDense] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [rows, setRows] = useState([]);
    const [showFilter, setShowFilter] = useState(false);

    const [nameFilter, setNameFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [idFilter, setIdFilter] = useState('');
    const [startDateFilter, setStarDateFilter] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    const [durationFilter, setDurationFilter] = useState('');
    const [machineFilter, setMachineFilter] = useState('');

    // delete confirm
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");

    const { token } = useToken();
    const navigate = useNavigate();

    const fetchJobs = () => {
        navigate('/jobs');

        const params = [
            ['name', nameFilter],
            ['status', statusFilter],
            ['guid', idFilter],
            ['start_date', startDateFilter],
            ['end_date', endDateFilter],
            ['duration', durationFilter],
            ['machine', machineFilter],
        ];

        let queryParams = "";
        params.forEach(([k, v]) => {
            const vv = v ?? "";
            if (vv.length > 1) queryParams += `${k}=${encodeURIComponent(vv)}&`;
        });

        queryParams = queryParams.endsWith("&") ? queryParams.slice(0, -1) : queryParams;
        const query = queryParams ? `api/getjobs?${queryParams}` : "api/getjobs";

        fetch(query, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token,
            }
        })
            .then(res => res.json())
            .then(res => setRows(res.results || []))
            .catch(err => console.log(err));
    };

    useEffect(() => {
        fetchJobs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nameFilter, statusFilter, idFilter, startDateFilter, endDateFilter, durationFilter, machineFilter]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = visibleRows.map((n) => n.guid).filter(Boolean);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, guid) => {
        const selectedIndex = selected.indexOf(guid);
        let newSelected = [];

        if (selectedIndex === -1) newSelected = newSelected.concat(selected, guid);
        else if (selectedIndex === 0) newSelected = newSelected.concat(selected.slice(1));
        else if (selectedIndex === selected.length - 1) newSelected = newSelected.concat(selected.slice(0, -1));
        else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangeDense = (event) => setDense(event.target.checked);

    const isSelectedRow = (guid) => selected.indexOf(guid) !== -1;

    const showEmpty = rows.length === 0;

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    const handleShowFilterChange = () => setShowFilter((p) => !p);

    const clearFilters = () => {
        setNameFilter('');
        setStatusFilter('');
        setIdFilter('');
        setStarDateFilter('');
        setEndDateFilter('');
        setDurationFilter('');
        setMachineFilter('');
        setPage(0);
    };

    const hasAnyFilter = useMemo(() => {
        return (
            (nameFilter ?? "").length > 0 ||
            (statusFilter ?? "").length > 0 ||
            (idFilter ?? "").length > 0 ||
            (startDateFilter ?? "").length > 0 ||
            (endDateFilter ?? "").length > 0 ||
            (durationFilter ?? "").length > 0 ||
            (machineFilter ?? "").length > 0
        );
    }, [nameFilter, statusFilter, idFilter, startDateFilter, endDateFilter, durationFilter, machineFilter]);

    // local filtering (also keeps select-all sane)
    const visibleRows = useMemo(() => {
        return rows.filter((r) => {
            const nameOk = !nameFilter || (r.name || "").toLowerCase().includes(nameFilter.toLowerCase());
            const idOk = !idFilter || (r.guid || "").toLowerCase().includes(idFilter.toLowerCase());
            const statusOk = !statusFilter || (r.status || "").toLowerCase().includes(statusFilter.toLowerCase());
            const machineOk = !machineFilter || (r.machine || "").toLowerCase().includes(machineFilter.toLowerCase());
            const startOk = !startDateFilter || String(r.start_date || "").toLowerCase().includes(startDateFilter.toLowerCase());
            const endOk = !endDateFilter || String(r.end_date || "").toLowerCase().includes(endDateFilter.toLowerCase());
            const durOk = !durationFilter || String(r.duration || "").toLowerCase().includes(durationFilter.toLowerCase());
            return nameOk && idOk && statusOk && machineOk && startOk && endOk && durOk;
        });
    }, [rows, nameFilter, idFilter, statusFilter, machineFilter, startDateFilter, endDateFilter, durationFilter]);

    // drop selection that is not visible after filter changes
    useEffect(() => {
        const visibleIds = new Set(visibleRows.map((r) => r.guid));
        setSelected((prev) => prev.filter((id) => visibleIds.has(id)));
    }, [visibleRows]);

    // delete (bulk) — uses api/deletejob with { guid }
    const openDeleteConfirm = () => {
        setDeleteError("");
        setConfirmOpen(true);
    };
    const closeDeleteConfirm = () => {
        if (deleting) return;
        setConfirmOpen(false);
    };

    const confirmDeleteJobs = async () => {
        if (selected.length === 0) {
            setConfirmOpen(false);
            return;
        }

        setDeleting(true);
        setDeleteError("");

        try {
            await Promise.all(
                selected.map((guid) =>
                    fetch('api/deletejob', {
                        method: 'POST',
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": "Bearer " + token,
                        },
                        body: JSON.stringify({ guid }),
                    }).then(async (r) => {
                        if (!r.ok) {
                            const txt = await r.text().catch(() => "");
                            throw new Error(txt || `Delete failed for ${guid}`);
                        }
                    })
                )
            );

            setSelected([]);
            setConfirmOpen(false);
            fetchJobs();
        } catch (e) {
            setDeleteError(e?.message || "Failed to delete one or more jobs.");
        } finally {
            setDeleting(false);
        }
    };

    const EnhancedTableToolbar = (props) => {
        const { numSelected } = props;

        return (
            <Toolbar
                sx={{
                    pl: { sm: 2 },
                    pr: { xs: 1, sm: 1 },
                    ...(numSelected > 0 && {
                        bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                    }),
                }}
            >
                {numSelected > 0 ? (
                    <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
                        {numSelected} selected
                    </Typography>
                ) : (
                    <Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle" component="div" />
                )}

                {numSelected > 0 && (
                    <Tooltip title={`Delete (${numSelected})`}>
                        <IconButton onClick={openDeleteConfirm}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Toolbar>
        );
    };

    EnhancedTableToolbar.propTypes = {
        numSelected: PropTypes.number.isRequired,
    };

    return (
        <>
            {showEmpty ? (
                <Box sx={{ p: 4 }}>
                    {emptyState}
                </Box>
            ) : (
                <>
                    <JobsReadyState />

                        <Box sx={{ width: '100%', overflowWrap:'anywhere' }}>
                        <Paper
                            elevation={0}
                            variant="outlined"
                            sx={{
                                width: "100%",
                                mb: 2,
                                borderRadius: 3,
                                overflow: "hidden",
                            }}
                        >
                            {/* Header bar (styl jak w pozostałych) */}
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1.25,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    bgcolor: "background.paper",
                                }}
                            >
                                <Typography sx={{ fontWeight: 900 }} variant="subtitle1">
                                    Jobs
                                </Typography>

                                <Box sx={{ flex: 1 }} />

                                {selected.length > 0 && (
                                    <Chip size="small" label={`${selected.length} selected`} sx={{ mr: 0.5 }} />
                                )}

                                <Tooltip title="Refresh">
                                    <IconButton
                                        onClick={fetchJobs}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                        size="small"
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={showFilter ? "Hide filters" : "Show filters"}>
                                    <IconButton
                                        onClick={handleShowFilterChange}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: showFilter ? "primary.main" : "divider",
                                            borderRadius: 2,
                                        }}
                                        size="small"
                                    >
                                        <FilterListIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                {selected.length > 0 && (
                                    <Tooltip title={`Delete (${selected.length})`}>
                                        <IconButton
                                            onClick={openDeleteConfirm}
                                            sx={{
                                                border: "1px solid",
                                                borderColor: "error.main",
                                                borderRadius: 2,
                                                color: "error.main",
                                            }}
                                            size="small"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            <Divider />

                            {/* Filters */}
                            {showFilter && (
                                <Box sx={{ p: 2, bgcolor: "background.paper" }}>
                                    <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems={{ xs: "stretch", lg: "center" }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                            Filters
                                        </Typography>

                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ py: 1 }}>
                                                            <TextField value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} label="Name" variant="standard" />
                                                        </TableCell>

                                                        <TableCell sx={{ py: 1 }}>
                                                            <FormControl sx={{ minWidth: 140 }} variant="standard">
                                                                <InputLabel id="statusFilter-label">Status</InputLabel>
                                                                <Select
                                                                    labelId="statusFilter-label"
                                                                    id="statusFilter"
                                                                    value={statusFilter}
                                                                    onChange={(e) => setStatusFilter(e.target.value)}
                                                                    label="Status"
                                                                >
                                                                    <MenuItem value={''}>All</MenuItem>
                                                                    <MenuItem value={'Succesfull'}>Succesfull</MenuItem>
                                                                    <MenuItem value={'Failed'}>Failed</MenuItem>
                                                                    <MenuItem value={'Cancelled'}>Cancelled</MenuItem>
                                                                    <MenuItem value={'In progress'}>In progress</MenuItem>
                                                                    <MenuItem value={'Pending'}>Pending</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                        </TableCell>

                                                        <TableCell sx={{ py: 1 }}>
                                                            <TextField value={idFilter} onChange={(e) => setIdFilter(e.target.value)} label="ID" variant="standard" />
                                                        </TableCell>

                                                        <TableCell sx={{ py: 1 }}>
                                                            <TextField value={machineFilter} onChange={(e) => setMachineFilter(e.target.value)} label="Machine" variant="standard" />
                                                        </TableCell>

                                                        <TableCell sx={{ py: 1 }}>
                                                            <TextField value={startDateFilter} onChange={(e) => setStarDateFilter(e.target.value)} label="Start Date" variant="standard" />
                                                        </TableCell>

                                                        <TableCell sx={{ py: 1 }}>
                                                            <TextField value={endDateFilter} onChange={(e) => setEndDateFilter(e.target.value)} label="End Date" variant="standard" />
                                                        </TableCell>

                                                        <TableCell sx={{ py: 1 }}>
                                                            <TextField value={durationFilter} onChange={(e) => setDurationFilter(e.target.value)} label="Duration" variant="standard" />
                                                        </TableCell>

                                                        <TableCell align="right" sx={{ whiteSpace: "nowrap", py: 1 }}>
                                                            <Button
                                                                size="small"
                                                                onClick={clearFilters}
                                                                disabled={!hasAnyFilter}
                                                                sx={{ textTransform: "none", fontWeight: 700 }}
                                                            >
                                                                Clear
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                            </Table>
                                        </TableContainer>
                                    </Stack>
                                </Box>
                            )}

                            {/* Table */}
                            <TableContainer sx={{ maxHeight: dense ? 520 : 640 }}>
                                <Table
                                    stickyHeader
                                    sx={{ minWidth: 900 }}
                                    aria-labelledby="tableTitle"
                                    size={dense ? 'small' : 'medium'}
                                >
                                    <TableHead>
                                        <EnhancedTableHead
                                            numSelected={selected.length}
                                            order={order}
                                            orderBy={orderBy}
                                            onSelectAllClick={handleSelectAllClick}
                                            onRequestSort={handleRequestSort}
                                            rowCount={visibleRows.length}
                                        />
                                    </TableHead>

                                    <TableBody>
                                        {stableSort(visibleRows, getComparator(order, orderBy))
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((row, index) => {
                                                const isItemSelected = isSelectedRow(row.guid);
                                                const labelId = `enhanced-table-checkbox-${index}`;

                                                return (
                                                    <TableRow
                                                        hover
                                                        role="checkbox"
                                                        aria-checked={isItemSelected}
                                                        tabIndex={-1}
                                                        key={row.guid}
                                                        selected={isItemSelected}
                                                        sx={{ "&:hover td": { bgcolor: "action.hover" } }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                onClick={(event) => handleClick(event, row.guid)}
                                                                color="primary"
                                                                checked={isItemSelected}
                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                            />
                                                        </TableCell>

                                                        <TableCell component="th" id={labelId} scope="row" padding="none">
                                                            {row.name}
                                                        </TableCell>

                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                            {row.status == "Succesfull" && <ColorChips label={row.status} color='success' />}
                                                            {row.status == "Failed" && <ColorChips label={row.status} color='error' />}
                                                            {row.status == "In progress" && <ColorChips label={row.status} color='primary' />}
                                                            {row.status == "Pending" && <ColorChips label={row.status} color='secondary' />}
                                                            {row.status == "Cancelled" && <ColorChips label={row.status} color='warning' />}
                                                        </TableCell>

                                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                            {row.guid}
                                                        </TableCell>

                                                        <TableCell>{row.machine}</TableCell>
                                                        <TableCell>{row.start_date}</TableCell>
                                                        <TableCell>{row.end_date}</TableCell>
                                                        <TableCell>{row.duration}</TableCell>

                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                            {String(row.exception || "").length > 0 && (
                                                                <PopoverComponent content={row.exception} variant='outlined' title='Show' color='error' />
                                                            )}
                                                        </TableCell>

                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                            {row.status == "In progress" && (
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <KillJobButton guid={row.guid} />
                                                                    {/* zostawiam Twoje Stop (jeśli ma logikę gdzie indziej) */}
                                                                    <Button color="warning" size="small" sx={{ textTransform: "none", fontWeight: 800 }}>
                                                                        Stop
                                                                    </Button>
                                                                </Stack>
                                                            )}
                                                        </TableCell>

                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                            <JobActionsMenu row={row} />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}

                                        {emptyRows > 0 && (
                                            <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                                <TableCell colSpan={12} />
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={visibleRows.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Paper>

                        <FormControlLabel
                            control={<Switch checked={dense} onChange={handleChangeDense} />}
                            label="Dense padding"
                        />
                    </Box>

                    {/* ✅ Bulk delete confirmation */}
                    <Dialog open={confirmOpen} onClose={closeDeleteConfirm} maxWidth="sm" fullWidth>
                        <DialogTitle sx={{ fontWeight: 900 }}>
                            Delete {selected.length} job{selected.length === 1 ? "" : "s"}?
                        </DialogTitle>

                        <DialogContent dividers>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                This will delete the selected job{selected.length === 1 ? "" : "s"}. This action cannot be undone.
                            </Typography>

                            <Box
                                sx={{
                                    mt: 1,
                                    maxHeight: 180,
                                    overflow: "auto",
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    bgcolor: "action.hover",
                                    borderRadius: 2,
                                    p: 1.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                {selected.map((id) => (
                                    <Box key={id} sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {id}
                                    </Box>
                                ))}
                            </Box>

                            {deleteError && (
                                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                    {deleteError}
                                </Typography>
                            )}
                        </DialogContent>

                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={closeDeleteConfirm} disabled={deleting} sx={{ textTransform: "none" }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={confirmDeleteJobs}
                                disabled={deleting || selected.length === 0}
                                sx={{ textTransform: "none", fontWeight: 900 }}
                            >
                                {deleting ? "Deleting..." : "Delete"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </>
    );
}

JobTable.propTypes = {
    emptyState: PropTypes.node,
};
